import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, Eye, ShoppingCart, DollarSign, Package,
  ArrowLeft, Calendar, Users, Smartphone, Monitor, Globe, Clock,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ShopAnalytics = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [shopId, period]);

  const fetchAnalytics = async () => {
    try {
      const [overviewRes, trendsRes, topRes, visitorsRes] = await Promise.all([
        api.get(`/shop-analytics/shop/${shopId}/overview`),
        api.get(`/shop-analytics/shop/${shopId}/trends?days=${period}`),
        api.get(`/shop-analytics/shop/${shopId}/top-products?limit=5`),
        api.get(`/shop-analytics/shop/${shopId}/visitors?days=${period}`)
      ]);
      
      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setTopProducts(topRes.data);
      setVisitors(visitorsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Calculate trend indicator
  const getTrendIndicator = (current, previous) => {
    if (current > previous) return { icon: ArrowUp, color: 'text-green-500', label: 'Up' };
    if (current < previous) return { icon: ArrowDown, color: 'text-red-500', label: 'Down' };
    return { icon: Minus, color: 'text-gray-500', label: 'Same' };
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 text-center">
        <p>Shop not found</p>
      </div>
    );
  }

  // Calculate simple chart data
  const maxViews = Math.max(...(trends?.trends?.map(t => t.views) || [1]));
  const maxRevenue = Math.max(...(trends?.trends?.map(t => t.revenue) || [1]));

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="shop-analytics">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(`/my-shops/${shopId}/edit`)}
            className="p-2 hover:bg-white/10 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              Shop Analytics
            </h1>
            <p className="text-muted-foreground">{overview.shop_name}</p>
          </div>
          
          {/* Period Selector */}
          <div className="ml-auto">
            <select
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Eye} 
            label="Total Views" 
            value={overview.total_views} 
            color="blue"
          />
          <StatCard 
            icon={ShoppingCart} 
            label="Total Orders" 
            value={overview.total_orders} 
            color="green"
          />
          <StatCard 
            icon={DollarSign} 
            label="Revenue" 
            value={`₹${overview.total_revenue.toLocaleString()}`} 
            color="primary"
          />
          <StatCard 
            icon={TrendingUp} 
            label="Conversion Rate" 
            value={`${overview.conversion_rate}%`} 
            color="purple"
          />
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Views Trend */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              Views Trend
            </h3>
            <div className="h-48 flex items-end gap-1">
              {trends?.trends?.slice(-14).map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-blue-500/50 rounded-t hover:bg-blue-500 transition-colors"
                    style={{ height: `${(day.views / maxViews) * 100}%`, minHeight: '4px' }}
                    title={`${day.date}: ${day.views} views`}
                  />
                  <span className="text-xs text-muted-foreground mt-1 -rotate-45 origin-left">
                    {day.date.slice(5)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Revenue Trend
            </h3>
            <div className="h-48 flex items-end gap-1">
              {trends?.trends?.slice(-14).map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-primary/50 rounded-t hover:bg-primary transition-colors"
                    style={{ height: `${maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0}%`, minHeight: '4px' }}
                    title={`${day.date}: ₹${day.revenue}`}
                  />
                  <span className="text-xs text-muted-foreground mt-1 -rotate-45 origin-left">
                    {day.date.slice(5)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Products & Visitors Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Top Products by Revenue */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-green-500" />
              Top Products by Revenue
            </h3>
            <div className="space-y-3">
              {topProducts?.by_revenue?.length > 0 ? (
                topProducts.by_revenue.map((product, idx) => (
                  <div key={product.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    {product.images?.[0] && (
                      <img src={product.images[0]} alt="" className="w-10 h-10 rounded object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.total_sales} sold</p>
                    </div>
                    <span className="text-green-500 font-bold">₹{product.total_revenue}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No sales data yet</p>
              )}
            </div>
          </div>

          {/* Device Breakdown */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-purple-500" />
              Visitor Devices
            </h3>
            <div className="space-y-3">
              {visitors?.devices?.length > 0 ? (
                visitors.devices.map((device, idx) => {
                  const icons = {
                    mobile: Smartphone,
                    desktop: Monitor,
                    tablet: Monitor,
                    unknown: Globe
                  };
                  const Icon = icons[device.device] || Globe;
                  const percentage = visitors.total_views > 0 
                    ? Math.round((device.count / visitors.total_views) * 100) 
                    : 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="capitalize">{device.device}</span>
                          <span className="text-muted-foreground">{device.count} ({percentage}%)</span>
                        </div>
                        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-muted-foreground text-center py-4">No visitor data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Traffic Sources & Peak Hours */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Traffic Sources */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Traffic Sources
            </h3>
            <div className="space-y-3">
              {visitors?.referrers?.length > 0 ? (
                visitors.referrers.map((ref, idx) => {
                  const percentage = visitors.total_views > 0 
                    ? Math.round((ref.count / visitors.total_views) * 100) 
                    : 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="truncate max-w-[200px]">{ref.source}</span>
                          <span className="text-muted-foreground">{ref.count}</span>
                        </div>
                        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-muted-foreground text-center py-4">No traffic data yet</p>
              )}
            </div>
          </div>

          {/* Peak Hours */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Peak Activity Hours
            </h3>
            <div className="h-32 flex items-end gap-0.5">
              {visitors?.hourly_activity?.map((hour, idx) => {
                const maxHourly = Math.max(...visitors.hourly_activity.map(h => h.count));
                const percentage = maxHourly > 0 ? (hour.count / maxHourly) * 100 : 0;
                
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-yellow-500/50 rounded-t hover:bg-yellow-500 transition-colors"
                      style={{ height: `${percentage}%`, minHeight: '2px' }}
                      title={`${hour.hour}:00 - ${hour.count} visits`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>12 AM</span>
              <span>6 AM</span>
              <span>12 PM</span>
              <span>6 PM</span>
              <span>11 PM</span>
            </div>
          </div>
        </div>

        {/* Top Locations */}
        {visitors?.locations?.length > 0 && (
          <div className="glass-card p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-500" />
              Visitor Locations
            </h3>
            <div className="flex flex-wrap gap-2">
              {visitors.locations.map((loc, idx) => (
                <div key={idx} className="px-3 py-2 bg-white/5 rounded-lg flex items-center gap-2">
                  <span>{loc.location}</span>
                  <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-sm">
                    {loc.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/20',
    blue: 'text-blue-500 bg-blue-500/20',
    green: 'text-green-500 bg-green-500/20',
    purple: 'text-purple-500 bg-purple-500/20',
    yellow: 'text-yellow-500 bg-yellow-500/20'
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default ShopAnalytics;
